import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils/cn";

interface Tab {
  key: string;
  label: string;
  count?: number;
}

/** One entry in the overflow ("More") menu. `group` puts entries under a
 * small heading; entries without one render first, ungrouped. */
export interface OverflowItem {
  key: string;
  label: string;
  group?: string;
}

/** Tabs with a single sliding active-indicator (measured via offsetLeft/
 * offsetWidth, not getBoundingClientRect, so the row's own horizontal
 * scroll offset never needs to be subtracted) rather than each tab's own
 * border popping on/off - per the interaction spec's "the active indicator
 * should visually move rather than disappear/reappear abruptly." Tab
 * widths are intrinsic (label + optional count badge) and the row can
 * scroll, so this can't be done with pure CSS - it needs the actual
 * rendered button rects.
 *
 * `overflow` is optional: pass items to get a trailing "More" dropdown for
 * destinations that don't fit as tabs. Every existing call site that uses
 * Tabs for page-local entity tabs is unaffected by omitting it. */
export function Tabs({
  tabs,
  active,
  onChange,
  overflow,
  onOverflowSelect,
}: {
  tabs: Tab[];
  active: string;
  onChange: (key: string) => void;
  overflow?: OverflowItem[];
  onOverflowSelect?: (key: string) => void;
}) {
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const el = buttonRefs.current.get(active);
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    else setIndicator(null);
  }, [active, tabs]);

  useEffect(() => {
    function reposition() {
      const el = buttonRefs.current.get(active);
      if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    }
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [active]);

  return (
    // `min-w-0` is load-bearing: this renders inside flex rows (e.g.
    // ReportsPage's tabs/filters header) where a flex item's default
    // min-width is its content's intrinsic width, not 0 - without it, this
    // div never shrinks below "wide enough for every tab label," so the
    // `overflow-x-auto` below never has anything to scroll *within itself*
    // and the surrounding page grows wider than the viewport instead,
    // dragging the page header and the records below along with it when the
    // user scrolls right. With it, this bar alone gets the horizontal
    // scrollbar and everything else stays put.
    <div className="min-w-0 border-b border-slate-200">
      <nav className="relative -mb-px flex items-center gap-6 overflow-x-auto scrollbar-none">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            ref={(el) => {
              if (el) buttonRefs.current.set(tab.key, el);
              else buttonRefs.current.delete(tab.key);
            }}
            onClick={() => onChange(tab.key)}
            aria-current={active === tab.key ? "page" : undefined}
            className={cn(
              "flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-1 py-3 text-sm font-medium transition-colors duration-200 ease-out",
              active === tab.key ? "text-primary-800" : "text-slate-500 hover:border-slate-300 hover:text-slate-700",
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-xs transition-colors duration-200 ease-out",
                  active === tab.key ? "bg-primary-100 text-primary-800" : "bg-slate-100 text-slate-600",
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
        {overflow && overflow.length > 0 && (
          <OverflowMenu items={overflow} onSelect={(key) => (onOverflowSelect ?? onChange)(key)} />
        )}
        {indicator && (
          <span
            className="pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-primary-700 transition-all duration-200 ease-out"
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
      </nav>
    </div>
  );
}

/** The "More" dropdown.
 *
 * Rendered through a portal with `position: fixed` rather than an ordinary
 * absolutely-positioned child: AppShell's root is `overflow-hidden` and its
 * <main> is `overflow-y-auto`, so an in-flow absolute panel is clipped by
 * those ancestors - and jsdom reports it as perfectly visible, so a unit
 * test would never catch it. Anchoring to the trigger's viewport rect
 * sidesteps both scroll containers entirely. */
function OverflowMenu({ items, onSelect }: { items: OverflowItem[]; onSelect: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    // Closing on scroll rather than repositioning: the trigger can leave
    // the viewport inside <main>'s own scroll container, and a menu pinned
    // to a stale rect would float detached from it.
    function onScrollOrResize() {
      setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  const ungrouped = items.filter((i) => !i.group);
  const groupNames: string[] = [];
  for (const item of items) {
    if (item.group && !groupNames.includes(item.group)) groupNames.push(item.group);
  }

  function choose(key: string) {
    setOpen(false);
    onSelect(key);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          "flex items-center gap-1 whitespace-nowrap border-b-2 border-transparent px-1 py-3 text-sm font-medium transition-colors duration-200 ease-out",
          open ? "text-primary-800" : "text-slate-500 hover:border-slate-300 hover:text-slate-700",
        )}
      >
        More
        <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-3.5 w-3.5">
          <path d="m6 8 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: rect.top, right: rect.right }}
            className="z-50 max-h-[70vh] w-64 overflow-y-auto scrollbar-thin rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
          >
            {ungrouped.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                onClick={() => choose(item.key)}
                className="block w-full rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-primary-50 hover:text-primary-800"
              >
                {item.label}
              </button>
            ))}
            {groupNames.map((group, idx) => (
              <div key={group} className={cn(ungrouped.length > 0 || idx > 0 ? "mt-1.5" : undefined)}>
                <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{group}</p>
                {items
                  .filter((i) => i.group === group)
                  .map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      role="menuitem"
                      onClick={() => choose(item.key)}
                      className="block w-full rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-primary-50 hover:text-primary-800"
                    >
                      {item.label}
                    </button>
                  ))}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
